import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Users, CreditCard, LinkIcon,
  ArrowRight, Shield, Activity, LifeBuoy, ClipboardList, Stethoscope, Briefcase, FileText,
} from 'lucide-react';
import { callAdminListUsers } from '../../services/admin';

type Stats = {
  totalUsers: number;
  activeSubscriptions: number;
  loading: boolean;
};

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats>({ totalUsers: 0, activeSubscriptions: 0, loading: true });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { users } = await callAdminListUsers({ limit: 200 });
        let activeSubs = 0;
        for (const u of users) {
          if (u.entitlements) {
            for (const ent of Object.values(u.entitlements) as any[]) {
              if (ent.status === 'active' || ent.status === 'trialing') activeSubs++;
            }
          }
        }
        if (mounted) setStats({ totalUsers: users.length, activeSubscriptions: activeSubs, loading: false });
      } catch {
        if (mounted) setStats(s => ({ ...s, loading: false }));
      }
    })();
    return () => { mounted = false; };
  }, []);

  const cards = [
    {
      title: 'Users',
      description: 'Manage users, roles, and subscriptions',
      icon: Users,
      href: '/admin/users',
      stat: stats.loading ? '...' : String(stats.totalUsers),
      statLabel: 'Total users',
    },
    {
      title: 'Payment Links',
      description: 'Generate and manage payment links',
      icon: LinkIcon,
      href: '/admin/payment-links',
      stat: null,
      statLabel: null,
    },
    {
      title: 'Payments',
      description: 'View all payment transactions',
      icon: CreditCard,
      href: '/admin/payments',
      stat: null,
      statLabel: null,
    },
    {
      title: 'Audit Logs',
      description: 'Trace all system and payment events',
      icon: ClipboardList,
      href: '/admin/audit-logs',
      stat: null,
      statLabel: null,
    },
    {
      title: 'VetAxis Ops',
      description: 'Backfill VetAxis users, clinics, and memberships',
      icon: Stethoscope,
      href: '/admin/vetaxis',
      stat: null,
      statLabel: null,
    },
    {
      title: 'StaffAxis Ops',
      description: 'Users and audit operations',
      icon: Briefcase,
      href: '/admin/staffaxis',
      stat: null,
      statLabel: null,
    },
    {
      title: 'NoteAxis Ops',
      description: 'Users and audit operations',
      icon: FileText,
      href: '/admin/noteaxis',
      stat: null,
      statLabel: null,
    },
    {
      title: 'Help Desk',
      description: 'Client onboarding runbooks and email extracts',
      icon: LifeBuoy,
      href: '/admin/help-desk',
      stat: null,
      statLabel: null,
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
              <Shield className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
                Admin Panel
              </h1>
              <p className="text-slate-600 dark:text-gray-400">Manage users, payments, and subscriptions</p>
            </div>
          </div>
        </motion.div>

        {/* Stats Row */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="glass-effect rounded-2xl p-6 shadow-glass">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              <span className="text-sm font-medium text-slate-600 dark:text-gray-400">Total Users</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">
              {stats.loading ? (
                <span className="inline-block w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              ) : stats.totalUsers}
            </p>
          </div>
          <div className="glass-effect rounded-2xl p-6 shadow-glass">
            <div className="flex items-center gap-3 mb-2">
              <Activity className="w-5 h-5 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-slate-600 dark:text-gray-400">Active Subscriptions</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">
              {stats.loading ? (
                <span className="inline-block w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              ) : stats.activeSubscriptions}
            </p>
          </div>
        </motion.div>

        {/* Navigation Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {cards.map((card) => {
            const Icon = card.icon;
            return (
              <Link key={card.href} to={card.href}>
                <motion.div
                  whileHover={{ y: -4 }}
                  className="glass-effect glass-effect-hover rounded-2xl p-6 shadow-glass h-full flex flex-col cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
                      <Icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{card.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-gray-400 mb-4 flex-1">{card.description}</p>
                  {card.stat && (
                    <div className="pt-4 border-t border-slate-200 dark:border-gray-700">
                      <span className="text-2xl font-bold text-slate-900 dark:text-white">{card.stat}</span>
                      <span className="text-sm text-slate-500 dark:text-gray-400 ml-2">{card.statLabel}</span>
                    </div>
                  )}
                </motion.div>
              </Link>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;
